import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, ShieldCheck, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import Brand from "../Brand";

const PaymentPill = ({ children, className = "" }) => (
    <span className={`inline-flex h-7 items-center rounded-md border border-zinc-200 bg-white px-2 text-[10px] font-bold tracking-tight shadow-sm dark:border-white/10 dark:bg-white/5 ${className}`}>
        {children}
    </span>
);

const StoreBadge = ({ store }) => {
    const isPlay = store === "play";
    return (
        <Link
            to="/mobile-app"
            className="inline-flex h-10 min-w-[136px] items-center gap-2 rounded-lg bg-zinc-900 px-3 text-white shadow-sm transition-transform hover:scale-[1.02] dark:bg-white dark:text-zinc-900"
            data-testid={`footer-store-${store}`}
        >
            <span className="text-[18px]" aria-hidden>{isPlay ? "▶" : ""}</span>
            <span className="flex flex-col leading-tight text-left">
                <span className="text-[8px] uppercase tracking-wider opacity-70">{isPlay ? "GET IT ON" : "Download on the"}</span>
                <span className="text-[13px] font-semibold tracking-tight">{isPlay ? "Google Play" : "App Store"}</span>
            </span>
        </Link>
    );
};

const COMPANY_LINKS = [
    { to: "/about", label: "About Us" },
    { to: "/articles", label: "Health Articles" },
    { to: "/stories", label: "Health Stories" },
    { to: "/library", label: "Health Library" },
    { to: "/conditions", label: "Diseases & Health Conditions" },
    { to: "/products?category=ayurveda", label: "Ayurveda" },
    { to: "/generic-medicines", label: "Understanding Generic Medicines" },
    { to: "/brands", label: "All Brands" },
    { to: "/support", label: "Need Help" },
    { to: "/faq", label: "FAQ" },
    { to: "/account/security", label: "Security" },
    { to: "/savings-calculator", label: "Savings Calculator" },
];

const LEGAL_LINKS = [
    { to: "/legal/terms", label: "Terms & Conditions" },
    { to: "/legal/privacy", label: "Privacy Policy" },
    { to: "/legal/editorial", label: "Editorial Policy" },
    { to: "/legal/returns", label: "Returns & Cancellations" },
    { to: "/legal/lowest-price", label: "Lowest Price Guarantee T&C" },
];

const Footer = () => {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim() || !/.+@.+\..+/.test(email)) {
            toast.error("Please enter a valid email");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email: email.trim() });
            if (data.already_subscribed) {
                toast.success("You're already subscribed — thanks!");
            } else {
                toast.success("Subscribed! Watch your inbox for tips and offers.");
            }
            setEmail("");
        } catch {
            toast.error("Couldn't subscribe right now — please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer className="mt-24 bg-purple-50/70 text-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-300" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
                {/* Top brand row */}
                <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-zinc-200/70 pb-6 dark:border-white/10">
                    <Brand size="md" to="/" testId="footer-brand" />
                    <p className="max-w-md text-xs text-zinc-500 dark:text-zinc-400">
                        AI-powered healthcare commerce for modern India. Genuine medicines, vitamins and devices delivered with care.
                    </p>
                </div>

                {/* 4-column grid */}
                <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
                    {/* Column 1 — Company */}
                    <div data-testid="footer-col-company">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Company</h3>
                        <ul className="space-y-2.5 text-sm">
                            {COMPANY_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="hover:text-brand-700 dark:hover:text-brand-300">{l.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 2 — Social + Legal */}
                    <div data-testid="footer-col-social">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Social</h3>
                        <div className="flex gap-3">
                            <a href="https://instagram.com/pills.asia" target="_blank" rel="noreferrer" aria-label="Instagram" className="grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-700 shadow-sm transition-colors hover:bg-brand-100 hover:text-brand-700 dark:bg-white/10 dark:text-zinc-200 dark:hover:bg-brand-900/40" data-testid="footer-social-instagram">
                                <Instagram className="h-4 w-4" />
                            </a>
                            <a href="https://facebook.com/pills.asia" target="_blank" rel="noreferrer" aria-label="Facebook" className="grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-700 shadow-sm transition-colors hover:bg-brand-100 hover:text-brand-700 dark:bg-white/10 dark:text-zinc-200" data-testid="footer-social-facebook">
                                <Facebook className="h-4 w-4" />
                            </a>
                            <a href="https://youtube.com/@pills-asia" target="_blank" rel="noreferrer" aria-label="YouTube" className="grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-700 shadow-sm transition-colors hover:bg-brand-100 hover:text-brand-700 dark:bg-white/10 dark:text-zinc-200" data-testid="footer-social-youtube">
                                <Youtube className="h-4 w-4" />
                            </a>
                            <a href="https://linkedin.com/company/pills-asia" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-700 shadow-sm transition-colors hover:bg-brand-100 hover:text-brand-700 dark:bg-white/10 dark:text-zinc-200" data-testid="footer-social-linkedin">
                                <Linkedin className="h-4 w-4" />
                            </a>
                        </div>

                        <h3 className="mb-4 mt-8 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Legal</h3>
                        <ul className="space-y-2.5 text-sm">
                            {LEGAL_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="hover:text-brand-700 dark:hover:text-brand-300">{l.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 3 — Subscribe + Office */}
                    <div data-testid="footer-col-subscribe">
                        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Subscribe</h3>
                        <p className="mb-3 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                            Claim your complimentary health and fitness tips subscription and stay updated on our newest promotions.
                        </p>
                        <form onSubmit={subscribe} className="flex gap-2" data-testid="footer-newsletter-form">
                            <input
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs outline-none ring-brand-200 focus:ring-2 dark:border-white/10 dark:bg-white/5"
                                data-testid="footer-newsletter-input"
                                required
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="rounded-lg bg-brand-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 disabled:opacity-50"
                                data-testid="footer-newsletter-submit"
                            >
                                {busy ? "…" : "Subscribe"}
                            </button>
                        </form>

                        <h3 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Registered Office Address</h3>
                        <div className="space-y-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                            <p className="font-semibold text-zinc-900 dark:text-zinc-100">PILLS Healthcare Pvt Ltd</p>
                            <p>Unit A-1, Tech Park,</p>
                            <p>Chennai, Tamil Nadu, India - 600001</p>
                            <p><span className="font-semibold">CIN:</span> U62099TN2026PTC123456</p>
                            <p>
                                <span className="font-semibold">Telephone:</span>{" "}
                                <a href="tel:+919876543210" className="text-sky-600 hover:underline dark:text-sky-400">+91 98765 43210</a>
                            </p>
                        </div>

                        <h3 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Grievance Officer</h3>
                        <div className="space-y-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                            <p><span className="font-semibold">Name:</span> Rajesh Kumar</p>
                            <p>
                                <span className="font-semibold">Email:</span>{" "}
                                <a href="mailto:grievance@pills.asia" className="text-sky-600 hover:underline dark:text-sky-400">grievance@pills.asia</a>
                            </p>
                        </div>
                    </div>

                    {/* Column 4 — Download + Contact */}
                    <div data-testid="footer-col-app">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Download PILLS</h3>
                        <p className="mb-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">Manage your health with ease — download the PILLS app today!</p>
                        <p className="mb-4 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                            Get easy access to medicines, health information, doctor consultations, lab tests, and more. With our app, you'll never have to wait in line again.
                        </p>
                        <div className="mb-6 flex flex-wrap gap-2">
                            <StoreBadge store="play" />
                            <StoreBadge store="appstore" />
                        </div>

                        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">Contact Us</h3>
                        <p className="mb-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                            Our customer representative team is available 7 days a week from 8:00 AM - 10:00 PM.
                        </p>
                        <ul className="space-y-1.5 text-xs">
                            <li className="flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5 text-zinc-400" />
                                <a href="mailto:support@pills.asia" className="text-sky-600 hover:underline dark:text-sky-400">support@pills.asia</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Phone className="h-3.5 w-3.5 text-zinc-400" />
                                <a href="tel:+919876543210" className="hover:underline">+91 98765 43210</a>
                            </li>
                        </ul>
                        <p className="mt-3 text-[10px] uppercase tracking-wider text-zinc-400">App version v1.0.0</p>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-zinc-200/70 bg-white/40 backdrop-blur dark:border-white/10 dark:bg-zinc-950/40">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 py-5 text-xs text-zinc-500 lg:flex-row lg:items-center lg:px-12 dark:text-zinc-400">
                    <p>
                        © {new Date().getFullYear()} - PILLS | All rights reserved. Our content is for informational purposes only.{" "}
                        <Link to="/legal/content-disclaimer" className="underline hover:text-brand-700 dark:hover:text-brand-300">See additional information</Link>.
                    </p>

                    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:border-emerald-700/40 dark:bg-emerald-900/20 dark:text-emerald-400" data-testid="footer-trust-badge">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Verified medical content
                    </div>

                    <div className="flex items-center gap-2" data-testid="footer-payment-row">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400">Our Payment Partners</span>
                        <PaymentPill className="text-[#1A1F71]">VISA</PaymentPill>
                        <PaymentPill className="text-[#EB001B]">MC</PaymentPill>
                        <PaymentPill className="text-[#0080C8]">Maestro</PaymentPill>
                        <PaymentPill className="text-[#F37520]">UPI</PaymentPill>
                        <PaymentPill className="text-[#00BAF2]">Paytm</PaymentPill>
                        <PaymentPill>GPay</PaymentPill>
                        <PaymentPill>NetBanking</PaymentPill>
                        <PaymentPill className="text-emerald-700">COD</PaymentPill>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
